import type { Directive } from 'vue';

const observers = new WeakMap<HTMLImageElement, IntersectionObserver>();

/**
 * Closest scrolling ancestor. The observer has to watch the open list, not the
 * page viewport: the whole list fits in the viewport and would load at once.
 */
function scrollParent(el: HTMLElement): HTMLElement | null {
	let node = el.parentElement;
	while (node) {
		const { overflowY } = getComputedStyle(node);
		if (overflowY === 'auto' || overflowY === 'scroll') return node;
		node = node.parentElement;
	}
	return null;
}

/**
 * Loads an image only once it shows up on screen.
 *
 * The country list of the phone input has 244 flags, one file each. Loading
 * them with the form made `/flags/` the most requested path of the portal.
 */
export const vLazyFlag: Directive<HTMLImageElement, string> = {
	mounted(el, binding) {
		if (!('IntersectionObserver' in window)) {
			el.src = binding.value;
			return;
		}
		const observer = new IntersectionObserver(
			entries => {
				if (entries.some(entry => entry.isIntersecting)) {
					el.src = binding.value;
					observer.disconnect();
				}
			},
			{ root: scrollParent(el), rootMargin: '64px' }
		);
		observer.observe(el);
		observers.set(el, observer);
	},
	unmounted(el) {
		observers.get(el)?.disconnect();
		observers.delete(el);
	}
};
